import { useEffect } from 'react';
import { useSelector } from 'react-redux';

/**
 * IntercomWidget — injects the Intercom live chat widget.
 *
 * Identifies the current user if logged in.
 * Renders nothing — only manages the Intercom script lifecycle.
 * Reads currentUser from Redux store directly.
 *
 * ENV:
 *   REACT_APP_INTERCOM_APP_ID — from Intercom workspace settings
 *
 * Usage:
 *   <IntercomWidget />
 */
const IntercomWidget = () => {
  const currentUser = useSelector(state => state.user?.currentUser);
  const appId = process.env.REACT_APP_INTERCOM_APP_ID;

  useEffect(() => {
    if (typeof window === 'undefined' || !appId) return;

    // Boot or update Intercom
    const profile = currentUser?.attributes?.profile;
    const email = currentUser?.attributes?.email;
    const userId = currentUser?.id?.uuid;

    const intercomSettings = {
      app_id: appId,
      ...(userId && {
        user_id: userId,
        email,
        name: [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || undefined,
      }),
    };

    if (window.Intercom) {
      window.Intercom('update', intercomSettings);
      return;
    }

    // Load Intercom snippet
    (function() {
      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', intercomSettings);
      } else {
        const d = document;
        const i = function() { i.c(arguments); };
        i.q = [];
        i.c = function(args) { i.q.push(args); };
        w.Intercom = i;
        const l = function() {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = `https://widget.intercom.io/widget/${appId}`;
          const x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();

    window.Intercom('boot', intercomSettings);
  }, [appId, currentUser?.id?.uuid]); // eslint-disable-line

  // Shutdown on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, []);

  return null;
};

export default IntercomWidget;
